import { Option } from '~/components/Select';
import { distanceUnitMap } from '~/constants/distance';
import { weightUnitMap } from '~/constants/weight';
import * as moment from 'moment';
import { PackageInformationFormValues } from '~/pages/Batch/PackageInformation/PackageInformationForm/PackageInformationForm';
import CarrierParcelTemplate from '~/types/CarrierParcelTemplate';
import ParcelTemplate from '~/types/ParcelTemplate';
import { ShipmentStatus } from '~/types/Shipment';
import SignatureConfirmation from '~/types/SignatureConfirmation';
import { TransactionStatus } from '~/types/Transaction';
import Address, { addressToJSON, BaseAddress, JSONBaseAddress } from './Address';
import APIAddress from './APIAddress';
import { isParcelTemplate } from './ParcelTemplate';

enum BatchGroupStatus {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  COMPLETE = 'COMPLETE',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
}

enum BatchEntryStatus {
  PENDING = 'PENDING',
  VALIDATING_ORDER = 'VALIDATING_ORDER',
  VALID_ORDER = 'VALID_ORDER',
  INVALID_ORDER = 'INVALID_ORDER',
  PROCESSING_SHIPMENT = 'PROCESSING_SHIPMENT',
  COMPLETE_SHIPMENT = 'COMPLETE_SHIPMENT',
  EXCLUDED_ORDER = 'EXCLUDED_ORDER',
  SHIPMENT_REQUEST_FAILED = 'SHIPMENT_REQUEST_FAILED',
  PROCESSING_TRANSACTION = 'PROCESSING_TRANSACTION',
  COMPLETE_TRANSACTION = 'COMPLETE_TRANSACTION',
  TRANSACTION_REQUEST_FAILED = 'TRANSACTION_REQUEST_FAILED',
  SHIPMENT_REQUEST_NOT_AVAILABLE = 'SHIPMENT_REQUEST_NOT_AVAILABLE',
  RATE_NOT_AVAILABLE = 'RATE_NOT_AVAILABLE',
}

type JSONBatchTransaction = {
  object_id: string;
  object_created: string;
  object_updated: string;
  object_status: string;
  batch_entry_id: number;
};

type JSONBatchShipment = {
  object_id: string;
  object_created: string;
  object_updated: string;
  object_status: string;
  num_rates: number;
  selected_rate_object_id: string;
  batch_entry_id: number;
};

type JSONBatchEntry = {
  id: number;
  object_created: string;
  object_updated: string;
  order_object_id: string;
  object_status: string;
  is_return: boolean;
};

type JSONBatchOrder = {
  object_id: string;
  object_created: string;
  object_updated: string;
  validation_status: Maybe<string>;
  errors: string[];
};

type JSONOrdersBatch = {
  id: number;
  object_created: string;
  object_updated: string;
  order_group_status: string;
  shipment_group_status: string;
  transaction_group_status: string;
  return_shipment_group_status: string;
  return_transaction_group_status: string;
  orders: JSONBatchOrder[];
  batch_entries: JSONBatchEntry[];
  shipments: JSONBatchShipment[];
  transactions: JSONBatchTransaction[];
  label_limit_renewal_date?: string;
  remaining_labels_until_limit?: number;
};

type JSONUserBatchInput = {
  shipment_date: string;
  parcel: {
    length: string;
    width: string;
    height: string;
    distance_unit: string;
    weight: string;
    weight_unit: string;
    use_imported_weight: boolean;
    template_token?: string;
    user_parcel_template_object_id?: Maybe<string>;
  };
  address_from?: JSONBaseAddress;
  address_from_id?: Maybe<number>;
  address_return?: JSONBaseAddress;
  address_return_id?: Maybe<number>;
  extra: {
    signature_confirmation?: SignatureConfirmation;
    is_residential?: boolean;
    is_return: boolean;
    insurance: {
      amount: string;
      currency: string;
      use_imported_amount: boolean;
      content: string;
    };
  };
};

type ParcelFormValues = {
  weight: string;
  weightUnit: Option;
  parcelTemplate: Maybe<ParcelTemplate & CarrierParcelTemplate>;
  useImportedWeight: boolean;
  customDimensions: Maybe<{
    width: string;
    length: string;
    height: string;
    distanceUnit: Option;
  }>;
};

type Parcel = {
  length: string;
  width: string;
  height: string;
  distanceUnit: string;
  weight: string;
  weightUnit: string;
  useImportedOrderWeight: boolean;
  templateToken?: string;
  userParcelTemplateId?: Maybe<string>;
};

type JSONParcel = {
  length: string;
  width: string;
  height: string;
  distance_unit: string;
  weight: string;
  mass_unit: string;
  useImportedOrderWeight: boolean;
  template: string;
};

function parcelFromJSON(jsonParcel: JSONParcel): Parcel {
  return {
    length: jsonParcel.length,
    width: jsonParcel.width,
    height: jsonParcel.height,
    distanceUnit: jsonParcel.distance_unit,
    weight: jsonParcel.weight,
    weightUnit: jsonParcel.mass_unit,
    useImportedOrderWeight: false, // TODO: this should not even be here
    templateToken: jsonParcel.template,
  };
}

type UserBatchInput = {
  shipmentDate: moment.Moment;
  parcel: Parcel;
  addressFrom?: BaseAddress;
  addressReturn?: BaseAddress;
  extra: {
    signatureConfirmation: boolean;
    isResidential?: boolean;
    isReturn: boolean;
    insurance: {
      amount: string;
      currency: string;
      useImportedOrderValue: boolean;
      content: string;
    };
  };
};

type BatchTransaction = {
  objectId: string;
  objectCreated: moment.Moment;
  objectUpdated: moment.Moment;
  objectStatus: TransactionStatus;
  batchEntryId: number;
};

type BatchShipment = {
  objectId: string;
  objectCreated: moment.Moment;
  objectUpdated: moment.Moment;
  objectStatus: ShipmentStatus;
  numRates: number;
  selectedRateObjectId: string;
  batchEntryId: number;
};

type BatchEntryId = number;

type BatchEntry = {
  id: BatchEntryId;
  objectCreated: moment.Moment;
  objectUpdated: moment.Moment;
  orderObjectId: string;
  objectStatus: BatchEntryStatus;
  isReturn: boolean;
  userInput?: UserBatchInput;

  /**
   * This is only used for return labels
   * It's the ID of the original order
   */
  returnOfID?: number | null;
};

type BatchOrder = {
  objectId: string;
  objectCreated: moment.Moment;
  objectUpdated: moment.Moment;
  validationStatus: Maybe<string>;
  errors: string[];
};

type OrdersBatch = {
  id: number;
  objectCreated: moment.Moment;
  objectUpdated: moment.Moment;
  orderGroupStatus: BatchGroupStatus;
  shipmentGroupStatus: BatchGroupStatus;
  transactionGroupStatus: BatchGroupStatus;
  returnShipmentGroupStatus: BatchGroupStatus;
  returnTransactionGroupStatus: BatchGroupStatus;
  orders: BatchOrder[];
  batchEntries: BatchEntry[];
  shipments: BatchShipment[];
  transactions: BatchTransaction[];
  remainingLabels?: number;
  labelLimitRenewalDate?: string;
};

enum BatchSteps {
  NONE = 'NONE',
  ORDERS_PAGE = 'ORDERS_PAGE',
  PACKAGE_INFORMATION = 'PACKAGE_INFORMATION',
  RATES = 'RATES',
  LABELS = 'LABELS',
}

function batchTransactionFromJSON(jsonBatchTransaction: JSONBatchTransaction): BatchTransaction {
  return {
    objectId: jsonBatchTransaction.object_id,
    objectCreated: moment(jsonBatchTransaction.object_created),
    objectUpdated: moment(jsonBatchTransaction.object_updated),
    objectStatus: <TransactionStatus>jsonBatchTransaction.object_status,
    batchEntryId: jsonBatchTransaction.batch_entry_id,
  };
}

function batchShipmentFromJSON(jsonBatchShipment: JSONBatchShipment): BatchShipment {
  return {
    objectId: jsonBatchShipment.object_id,
    objectCreated: moment(jsonBatchShipment.object_created),
    objectUpdated: moment(jsonBatchShipment.object_updated),
    objectStatus: <ShipmentStatus>jsonBatchShipment.object_status,
    numRates: jsonBatchShipment.num_rates,
    batchEntryId: jsonBatchShipment.batch_entry_id,
    selectedRateObjectId: jsonBatchShipment.selected_rate_object_id,
  };
}

function batchEntriesFromJSON(jsonBatchEntry: JSONBatchEntry): BatchEntry {
  return {
    id: jsonBatchEntry.id,
    objectCreated: moment(jsonBatchEntry.object_created),
    objectUpdated: moment(jsonBatchEntry.object_updated),
    orderObjectId: jsonBatchEntry.order_object_id,
    objectStatus: <BatchEntryStatus>jsonBatchEntry.object_status,
    isReturn: jsonBatchEntry.is_return,
  };
}

function batchOrderFromJSON(jsonBatchOrder: JSONBatchOrder): BatchOrder {
  return {
    objectId: normalizeObjectId(jsonBatchOrder.object_id),
    objectCreated: moment(jsonBatchOrder.object_created),
    objectUpdated: moment(jsonBatchOrder.object_updated),
    validationStatus: jsonBatchOrder.validation_status,
    errors: jsonBatchOrder.errors,
  };
}

function fromJSON(jsonOrdersBatch: JSONOrdersBatch): OrdersBatch {
  return {
    id: jsonOrdersBatch.id,
    objectCreated: moment(jsonOrdersBatch.object_created),
    objectUpdated: moment(jsonOrdersBatch.object_updated),
    orderGroupStatus: <BatchGroupStatus>jsonOrdersBatch.order_group_status,
    shipmentGroupStatus: <BatchGroupStatus>jsonOrdersBatch.shipment_group_status,
    transactionGroupStatus: <BatchGroupStatus>jsonOrdersBatch.transaction_group_status,
    returnShipmentGroupStatus: <BatchGroupStatus>jsonOrdersBatch.return_shipment_group_status,
    returnTransactionGroupStatus: <BatchGroupStatus>jsonOrdersBatch.return_transaction_group_status,
    orders: jsonOrdersBatch.orders.map(batchOrderFromJSON),
    batchEntries: jsonOrdersBatch.batch_entries.map(batchEntriesFromJSON),
    shipments: jsonOrdersBatch.shipments.map(batchShipmentFromJSON),
    transactions: jsonOrdersBatch.transactions.map(batchTransactionFromJSON),
    labelLimitRenewalDate: jsonOrdersBatch.label_limit_renewal_date,
    remainingLabels: jsonOrdersBatch.remaining_labels_until_limit,
  };
}

function userBatchInputToJSON(userBatchInput: UserBatchInput): JSONUserBatchInput {
  return {
    shipment_date: moment(userBatchInput.shipmentDate)
      .startOf('day')
      .utc()
      .endOf('day')
      .toISOString(),
    parcel: {
      length: userBatchInput.parcel.length,
      width: userBatchInput.parcel.width,
      height: userBatchInput.parcel.height,
      distance_unit: userBatchInput.parcel.distanceUnit,
      weight: userBatchInput.parcel.weight,
      weight_unit: userBatchInput.parcel.weightUnit,
      use_imported_weight: userBatchInput.parcel.useImportedOrderWeight,
      template_token: userBatchInput.parcel.templateToken,
      user_parcel_template_object_id: userBatchInput.parcel.userParcelTemplateId,
    },
    address_from: addressToJSON(userBatchInput.addressFrom as Address),
    address_return: addressToJSON(userBatchInput.addressReturn as Address),
    address_from_id: userBatchInput.addressFrom
      ? (userBatchInput.addressFrom as APIAddress).id
      : null,
    address_return_id: userBatchInput.addressReturn
      ? (userBatchInput.addressReturn as APIAddress).id
      : null,
    extra: {
      ...(userBatchInput.extra.signatureConfirmation
        ? { signature_confirmation: SignatureConfirmation.STANDARD }
        : {}),
      is_residential: userBatchInput.extra.isResidential,
      is_return: userBatchInput.extra.isReturn,
      insurance: {
        amount: userBatchInput.extra.insurance.amount,
        currency: userBatchInput.extra.insurance.currency,
        use_imported_amount: userBatchInput.extra.insurance.useImportedOrderValue,
        content: userBatchInput.extra.insurance.content,
      },
    },
  };
}

function userBatchInputFromJSON(jsonUserBatchInput: JSONUserBatchInput): UserBatchInput {
  return {
    shipmentDate: moment(jsonUserBatchInput.shipment_date),
    parcel: {
      length: jsonUserBatchInput.parcel.length,
      width: jsonUserBatchInput.parcel.width,
      height: jsonUserBatchInput.parcel.height,
      distanceUnit: distanceUnitMap[jsonUserBatchInput.parcel.distance_unit],
      weight: jsonUserBatchInput.parcel.weight,
      weightUnit: weightUnitMap[jsonUserBatchInput.parcel.weight_unit],
      useImportedOrderWeight: jsonUserBatchInput.parcel.use_imported_weight,
      templateToken: jsonUserBatchInput.parcel.template_token,
      userParcelTemplateId: jsonUserBatchInput.parcel.user_parcel_template_object_id,
    },
    extra: {
      signatureConfirmation: !!jsonUserBatchInput.extra.signature_confirmation,
      isResidential: false, // TODO: I don't think we're using this field
      isReturn: false, // TODO: not necessary in inline edits yet
      insurance: {
        amount: jsonUserBatchInput.extra.insurance.amount,
        currency: jsonUserBatchInput.extra.insurance.currency,
        useImportedOrderValue: jsonUserBatchInput.extra.insurance.use_imported_amount,
        content: jsonUserBatchInput.extra.insurance.content,
      },
    },
  };
}

function getParcelData(data: PackageInformationFormValues): Parcel {
  // If updating, check if Parcel.getParcelData() needs update too
  if (data.extras.isUsingCustomDimensions && data.parcel.customDimensions) {
    return {
      length: data.parcel.customDimensions.length,
      width: data.parcel.customDimensions.width,
      height: data.parcel.customDimensions.height,
      distanceUnit: data.parcel.customDimensions.distanceUnit.value.toString(),
      weight: data.parcel.weight,
      weightUnit: data.parcel.weightUnit.value.toString(),
      useImportedOrderWeight: data.parcel.useImportedWeight,
    };
  }

  const parcelTemplate = data.parcel.parcelTemplate;
  // if it's a user parcel template, get the dimensions from the template
  if (parcelTemplate && isParcelTemplate(parcelTemplate)) {
    // if it's derived from a carrier template, the dimensions are from the nested template
    if (parcelTemplate.carrierTemplate) {
      return {
        // TODO: remove dimensions when BE supports that for carrier templates
        length: parcelTemplate.carrierTemplate.length.toString(),
        width: parcelTemplate.carrierTemplate.width.toString(),
        height: parcelTemplate.carrierTemplate.height.toString(),
        distanceUnit: distanceUnitMap[parcelTemplate.carrierTemplate.distanceUnit],
        weight: data.parcel.weight,
        weightUnit: weightUnitMap[String(data.parcel.weightUnit.value)],
        useImportedOrderWeight: data.parcel.useImportedWeight,
        templateToken: parcelTemplate.carrierTemplate.token,
      };
    }
    // otherwise the dimensions are from the parcel template
    return {
      length: parcelTemplate.length.toString(),
      width: parcelTemplate.width.toString(),
      height: parcelTemplate.height.toString(),
      distanceUnit: distanceUnitMap[parcelTemplate.distanceUnit],
      weight: data.parcel.weight,
      weightUnit: weightUnitMap[String(data.parcel.weightUnit.value)],
      useImportedOrderWeight: data.parcel.useImportedWeight,
      userParcelTemplateId: parcelTemplate.objectId,
    };
  }

  // otherwise this is a carrier template
  return {
    // TODO: remove dimensions when BE supports that for carrier templates
    length: parcelTemplate!.length.toString(),
    width: parcelTemplate!.width.toString(),
    height: parcelTemplate!.height.toString(),
    distanceUnit: distanceUnitMap[parcelTemplate!.distanceUnit],
    weight: data.parcel.weight,
    weightUnit: weightUnitMap[String(data.parcel.weightUnit.value)],
    useImportedOrderWeight: data.parcel.useImportedWeight,
    templateToken: parcelTemplate!.token,
  };
}

function isFailedBatchEntry(batchEntry: BatchEntry) {
  return (
    batchEntry.objectStatus === BatchEntryStatus.INVALID_ORDER ||
    batchEntry.objectStatus === BatchEntryStatus.SHIPMENT_REQUEST_FAILED ||
    batchEntry.objectStatus === BatchEntryStatus.TRANSACTION_REQUEST_FAILED
  );
}

// Move to utils
function normalizeObjectId(objectId: string): string {
  return objectId.replace(new RegExp('-', 'g'), '');
}

export default OrdersBatch;
export {
  fromJSON,
  BatchOrder,
  BatchGroupStatus,
  batchTransactionFromJSON,
  UserBatchInput,
  JSONUserBatchInput,
  userBatchInputToJSON,
  Parcel,
  parcelFromJSON,
  JSONParcel,
  BatchSteps,
  BatchTransaction,
  BatchShipment,
  BatchEntryStatus,
  BatchEntry,
  isFailedBatchEntry,
  getParcelData,
  userBatchInputFromJSON,
  ParcelFormValues,
  batchEntriesFromJSON,
  JSONBatchEntry,
  JSONOrdersBatch,
  OrdersBatch,
  BatchEntryId,
};
